import {
  MAX_CHORD_LENGTH,
  MAX_SAGITTA,
  MAX_WALL_LENGTH,
  MIN_CHORD_LENGTH,
  MIN_SAGITTA,
  MIN_WALL_LENGTH
} from '@/editor/constant/wall';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { RulerGuideLineModel } from '@/editor/entities/2d/rulerGuideLineModel';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { getRelativeWallRulerInfos } from '@/editor/stores/utils/ruler/sepWall';
import { EWallOperateType } from '@/editor/types';
import { showToast } from '@/editor/utils/feedback';
import { clamp } from 'lodash-es';
import i18n from '@/i18n';

type ISepWallRulerInfo = ReturnType<typeof getRelativeWallRulerInfos>[number];

export class SepWallRulerController {
  private _mainSepId: string = '';
  private _chordGuideLineModel = new RulerGuideLineModel();
  private _rulerModel = new RulerModel<ISepWallRulerInfo>({
    onCommit: this.rulerOnChange.bind(this),
    onChange: this.rulerOnChange.bind(this)
  });

  constructor(wallId: string) {
    this._mainSepId = wallId;
  }

  // 标尺处理
  update() {
    if (!this._mainSepId) return;

    const mainSepWallData = globalStores.separateWallStore.getSepData(this._mainSepId, false);
    if (mainSepWallData?.center) {
      this._chordGuideLineModel.update([{ start: mainSepWallData.start, end: mainSepWallData.end }]);
    } else {
      this._chordGuideLineModel.clear();
    }

    this._rulerModel.updateModel(
      getRelativeWallRulerInfos(this._mainSepId).map((item, index) => ({
        ...item,
        id: `${index}`,
        focusTrap: true,
        blurOnCommit: true
      }))
    );
  }

  private rulerOnChange(id: string, value: number, rulerInfo: ISepWallRulerInfo) {
    const rulerSepWallData =
      globalStores.separateWallStore.getSepData(rulerInfo.sepId, false) ||
      globalStores.separateWallStore.getSepData(rulerInfo.sepId, true);
    const mainSepWallData = globalStores.separateWallStore.getSepData(this._mainSepId, false);
    if (!rulerSepWallData || !mainSepWallData) return;

    const mainSepWallStart = mainSepWallData.start;
    const mainSepWallEnd = mainSepWallData.end;
    const oldDistance = Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end);
    const newDistance = value;
    const diffDistance = newDistance - oldDistance;

    // 主隔墙端点变化
    if (rulerInfo.sepId === mainSepWallData.id) {
      const isArc = mainSepWallData.center && mainSepWallData.clockwise !== undefined;
      const clampedDiffDistance = isArc
        ? clamp(value, MIN_CHORD_LENGTH, MAX_CHORD_LENGTH) - oldDistance
        : clamp(value, MIN_WALL_LENGTH, MAX_WALL_LENGTH) - oldDistance;
      const clampedSagitta = isArc && rulerInfo.arcMidMovable ? clamp(value, MIN_SAGITTA, MAX_SAGITTA) : diffDistance;
      const direction = Point2dUtils.subtracted(mainSepWallEnd, mainSepWallStart);

      if (rulerInfo.arcMidMovable && mainSepWallData.center && mainSepWallData.clockwise !== undefined) {
        const currentArc = new ArcSeg2d({
          start: mainSepWallStart,
          end: mainSepWallEnd,
          center: mainSepWallData.center,
          clockwise: mainSepWallData.clockwise
        });
        const newSagitta = clampedSagitta * Math.sign(currentArc.getSagitta());
        const newArc = new ArcSeg2d({ start: mainSepWallStart, end: mainSepWallEnd, sagitta: newSagitta });
        // 通过弧中点操作走合法性校验流程（与其他端点修改一致），非法时给出提示并还原
        this.handleEditResult(
          globalStores.separateWallOperation.moveSeparateWallPoint(rulerInfo.sepId, {
            type: EWallOperateType.ArcMidPoint,
            point: newArc.getMidPoint()
          })
        );
      } else if (rulerInfo.endMovable) {
        const newEndPoint = Point2dUtils.addDistance(mainSepWallEnd, clampedDiffDistance, direction);
        this.handleEditResult(
          globalStores.separateWallOperation.moveSeparateWallPoint(rulerInfo.sepId, {
            type: EWallOperateType.EndPoint,
            point: newEndPoint
          })
        );
      } else if (rulerInfo.startMovable) {
        const newStartPoint = Point2dUtils.addDistance(mainSepWallStart, -clampedDiffDistance, direction);
        this.handleEditResult(
          globalStores.separateWallOperation.moveSeparateWallPoint(rulerInfo.sepId, {
            type: EWallOperateType.StartPoint,
            point: newStartPoint
          })
        );
      } else {
        this.update();
      }

      return;
    }

    // 平移墙体
    const mainSepWallSeg = new LineSeg2d(mainSepWallStart, mainSepWallEnd);
    const rulerMidPoint = Point2dUtils.getCenter(rulerInfo.start, rulerInfo.end);
    const side = Math.sign(mainSepWallSeg.pointLineSegSide(rulerMidPoint));
    const mainWallVerticalDirection = mainSepWallSeg.getPerpendicularDirectionVector();

    if (mainSepWallData.center) {
      const rulerDirection = Point2dUtils.getUnitDirection(rulerInfo.start, rulerInfo.end);
      const moveDirection = rulerInfo.startMovable ? { x: -rulerDirection.x, y: -rulerDirection.y } : rulerDirection;
      this.handleEditResult(globalStores.separateWallOperation.pan(mainSepWallData.id, diffDistance, moveDirection));
    } else {
      const rulerDirection = Point2dUtils.getUnitDirection(rulerInfo.end, rulerInfo.start);
      const moveDistance =
        -Math.abs(Point2dUtils.projectScalar(mainWallVerticalDirection, rulerDirection)) * diffDistance * side;

      this.handleEditResult(globalStores.separateWallOperation.pan(mainSepWallData.id, moveDistance));
    }
  }

  private handleEditResult(result: boolean | Promise<boolean>) {
    void Promise.resolve(result).then((isSuccess) => {
      if (!isSuccess) {
        showToast(i18n.t('draw.warning.sep_wall_intersecting'));
      }

      this.update();
    });
  }

  dispose() {
    this._chordGuideLineModel.dispose();
    this._rulerModel.dispose();
  }
}
